import { useParams, useNavigate } from "react-router-dom";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import { fetchSingleTask, updateTaskStatus } from "../../api/tasks";

export default function TaskDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    });

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [locationName, setLocationName] = useState("Loading...");

    // Image viewer state
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState("");

    // Action dialogs
    const [confirmAction, setConfirmAction] = useState(null);
    const [commentAction, setCommentAction] = useState(null);
    const [commentText, setCommentText] = useState("");
    const [activeTab, setActiveTab] = useState("overview");

    const handleStatusUpdate = async (fromStatus, toStatus, comment = null) => {
        try {
            setActionLoading(true);
            setActionError("");

            const payload = {
                taskId: task.id,
                fromStatus,
                toStatus,
            };

            if (comment) payload.comment = comment;

            const response = await updateTaskStatus(payload);
            if (!response.success) throw new Error(response.message);

            setTask(response.data);
            setConfirmAction(null);
            setCommentAction(null);
            setCommentText("");
        } catch (err) {
            setActionError(
                err.response?.data?.message ||
                    err.message ||
                    "Failed to update task status"
            );
        } finally {
            setActionLoading(false);
        }
    };

    /* ===============================
       FETCH TASK
    =============================== */
    useEffect(() => {
        const loadTask = async () => {
            try {
                setLoading(true);
                const response = await fetchSingleTask(id);
                if (!response || !response.id) {
                    throw new Error("Invalid task payload");
                }
                setTask(response);
            } catch (err) {
                console.error(err);
                setError("Failed to load task details.");
            } finally {
                setLoading(false);
            }
        };

        if (id) loadTask();
        else {
            setError("Missing task ID");
            setLoading(false);
        }
    }, [id]);

    /* ===============================
       REVERSE GEOCODE
    =============================== */
    useEffect(() => {
        if (!task?.location?.lat) {
            setLocationName("Unknown Location");
            return;
        }

        const { lat, lng, name } = task.location;

        fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}`
        )
            .then((res) => res.json())
            .then((data) =>
                setLocationName(data?.results?.[0]?.formatted_address || name)
            )
            .catch(() => setLocationName(name || "Unknown Location"));
    }, [task]);

    /* ===============================
       IMAGE VIEWER HELPERS
    =============================== */
    const openImageViewer = (images, index = 0) => {
        setViewerImages(images);
        setViewerIndex(index);
        setViewerOpen(true);
    };

    const closeViewer = () => {
        setViewerOpen(false);
        setViewerImages([]);
        setViewerIndex(0);
    };

    const nextImage = () => setViewerIndex((i) => (i + 1) % viewerImages.length);

    const prevImage = () =>
        setViewerIndex((i) => (i === 0 ? viewerImages.length - 1 : i - 1));

    /* ===============================
       STATES
    =============================== */
    if (loading) {
        return (
            <div className="dashboard-content space-y-1 animate-pulse text-gray-900 dark:text-gray-100">
                <div className="border border-gray-200 bg-white px-6 py-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3">
                                <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700" />
                                <div className="h-6 w-28 bg-gray-200 dark:bg-gray-700" />
                            </div>

                            <div className="mt-4 h-9 w-52 bg-gray-200 dark:bg-gray-700" />
                            <div className="mt-3 h-5 w-72 bg-gray-200 dark:bg-gray-700" />

                            <div className="mt-5 flex flex-wrap gap-3">
                                <div className="h-16 w-36 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                                <div className="h-16 w-36 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                                <div className="h-16 w-36 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <div className="h-11 w-32 bg-gray-200 dark:bg-gray-700" />
                            <div className="h-11 w-32 bg-gray-200 dark:bg-gray-700" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-0 lg:grid-cols-3">
                    <div className="border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                        <div className="mb-4 h-5 w-32 bg-gray-200 dark:bg-gray-700" />
                        <div className="space-y-3">
                            <div className="h-4 w-40 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-44 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-52 bg-gray-100 dark:bg-gray-800" />
                        </div>
                    </div>

                    <div className="border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                        <div className="mb-4 h-5 w-28 bg-gray-200 dark:bg-gray-700" />
                        <div className="space-y-3">
                            <div className="h-4 w-40 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-44 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-52 bg-gray-100 dark:bg-gray-800" />
                        </div>
                    </div>

                    <div className="border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                        <div className="mb-4 h-5 w-36 bg-gray-200 dark:bg-gray-700" />
                        <div className="space-y-3">
                            <div className="h-4 w-40 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-32 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-36 bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-36 bg-gray-100 dark:bg-gray-800" />
                        </div>
                    </div>

                    <div className="border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900 lg:col-span-3">
                        <div className="mb-4 h-5 w-32 bg-gray-200 dark:bg-gray-700" />
                        <div className="space-y-3">
                            <div className="h-4 w-full bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-full bg-gray-100 dark:bg-gray-800" />
                            <div className="h-4 w-3/4 bg-gray-100 dark:bg-gray-800" />
                        </div>
                    </div>
                </div>

                <div className="border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                    <div className="mb-5 h-5 w-40 bg-gray-200 dark:bg-gray-700" />
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="h-16 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                        <div className="h-16 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                        <div className="h-16 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                        <div className="h-16 border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800" />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-content py-20 text-gray-900 dark:text-gray-100">
                <h1 className="dashboard-page-title dark:text-white">Task Not Found</h1>
                <p className="mt-3 text-gray-500 dark:text-gray-400">{error}</p>
                <button
                    onClick={() => navigate("/tasks")}
                    className="btn btn-primary mt-6"
                >
                    Back to Tasks
                </button>
            </div>
        );
    }

    const show = (v) => (v !== null && v !== undefined && v !== "" ? v : "—");

    const mapCenter = task.location?.lat
        ? { lat: task.location.lat, lng: task.location.lng }
        : { lat: -1.286389, lng: 36.817223 };

    /* ===============================
       RENDER
    =============================== */
    return (
        <div className="dashboard-content space-y-1 text-gray-900 dark:text-gray-100">
            {/* HEADER */}
            <div className="border border-gray-200 bg-white px-6 py-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                                Task Overview
                            </p>

                            <span className="inline-flex items-center border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300">
                                {task.status}
                            </span>
                        </div>

                        <h1 className="dashboard-page-title mt-3 !mb-0 text-gray-900 dark:text-white">
                            Task #{task.id}
                        </h1>

                        <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                            {task.title}
                        </p>

                        <div className="mt-4 flex flex-wrap gap-3">
                            <div className="border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    Technician
                                </p>
                                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {show(task.technician?.name)}
                                </p>
                            </div>

                            <div className="border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    Customer
                                </p>
                                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {show(task.customer?.name)}
                                </p>
                            </div>

                            <div className="border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    Due Date
                                </p>
                                <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {show(task.dueDate)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 xl:justify-end">
                        {task.status === "PENDING_APPROVAL" && (
                            <>
                                <button
                                    onClick={() =>
                                        setConfirmAction({
                                            fromStatus: "PENDING_APPROVAL",
                                            toStatus: "PENDING_ACCEPTANCE",
                                            label: "Approve Task",
                                        })
                                    }
                                    className="bg-green-600 px-5 py-2.5 font-medium text-white shadow-sm transition hover:bg-green-700"
                                >
                                    Approve
                                </button>

                                <button
                                    onClick={() =>
                                        setCommentAction({
                                            fromStatus: "PENDING_APPROVAL",
                                            toStatus: "REJECTED",
                                            label: "Reject Task",
                                        })
                                    }
                                    className="bg-red-600 px-5 py-2.5 font-medium text-white shadow-sm transition hover:bg-red-700"
                                >
                                    Reject
                                </button>
                            </>
                        )}

                        {task.status === "PENDING_INSPECTION" && (
                            <>
                                <button
                                    onClick={() =>
                                        setConfirmAction({
                                            fromStatus: "PENDING_INSPECTION",
                                            toStatus: "COMPLETED",
                                            label: "Complete Task",
                                        })
                                    }
                                    className="bg-blue-600 px-5 py-2.5 font-medium text-white shadow-sm transition hover:bg-blue-700"
                                >
                                    Complete
                                </button>

                                <button
                                    onClick={() =>
                                        setCommentAction({
                                            fromStatus: "PENDING_INSPECTION",
                                            toStatus: "IN_PROGRESS",
                                            label: "Return to In Progress",
                                        })
                                    }
                                    className="bg-amber-500 px-5 py-2.5 font-medium text-white shadow-sm transition hover:bg-amber-600"
                                >
                                    Return
                                </button>
                            </>
                        )}

                        <button
                            onClick={() => navigate("/tasks")}
                            className="border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                            Back to Tasks
                        </button>
                    </div>
                </div>

                {actionError && (
                    <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-950/30">
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">
                            {actionError}
                        </p>
                    </div>
                )}
            </div>

            <div className="border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
                <div className="flex flex-wrap">
                    {[
                        { key: "overview", label: "Overview" },
                        { key: "checklist", label: "Checklist" },
                        { key: "location", label: "Location" },
                        {
                            key: "comments",
                            label: `Comments (${task.comments?.length || 0})`,
                        },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`border-r border-gray-200 px-5 py-3 text-sm font-medium transition dark:border-gray-800 ${
                                activeTab === tab.key
                                    ? "bg-black text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* DETAILS GRID */}
            {activeTab === "overview" && (
                <div className="grid grid-cols-1 gap-0 lg:grid-cols-3">
                    <div className="dashboard-card-straight rounded-none border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                        <h2 className="section-title dark:text-white">Technician</h2>
                        <p className="dark:text-gray-300">Name: {show(task.technician?.name)}</p>
                        <p className="dark:text-gray-300">Phone: {show(task.technician?.phone)}</p>
                        <p className="dark:text-gray-300">Email: {show(task.technician?.email)}</p>
                    </div>

                    <div className="dashboard-card-straight rounded-none border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                        <h2 className="section-title dark:text-white">Customer</h2>
                        <p className="dark:text-gray-300">Name: {task.customer.name}</p>
                        <p className="dark:text-gray-300">Phone: {task.customer.phone}</p>
                        <p className="dark:text-gray-300">Location: {task.customer.location}</p>
                    </div>

                    <div className="dashboard-card-straight rounded-none border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                        <h2 className="section-title dark:text-white">Task Timelines</h2>
                        <div className="space-y-1 dark:text-gray-300">
                            <p>
                                <strong className="dark:text-white">Assigned:</strong>{" "}
                                {show(task.assignedDate)}
                            </p>
                            <p>
                                <strong className="dark:text-white">Due:</strong>{" "}
                                {show(task.dueDate)}
                            </p>
                            <p>
                                <strong className="dark:text-white">Estimated Hours:</strong>{" "}
                                {show(task.estimatedHours)}
                            </p>
                            <p>
                                <strong className="dark:text-white">Actual Hours:</strong>{" "}
                                {show(task.actualHours)}
                            </p>
                        </div>
                    </div>

                    <div className="dashboard-card-straight rounded-none border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900 lg:col-span-3">
                        <h2 className="section-title dark:text-white">Description</h2>
                        <p className="leading-relaxed text-gray-700 dark:text-gray-300">
                            {task.description}
                        </p>
                    </div>
                </div>
            )}

            {activeTab === "checklist" && (
                <div className="dashboard-card-straight border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                    <h2 className="section-title dark:text-white">Task Checklist</h2>
                    {!task.checklist?.length ? (
                        <p className="text-gray-500 dark:text-gray-400">No checklist items.</p>
                    ) : (
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                            {task.checklist.map((c, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
                                >
                                    <input
                                        type="checkbox"
                                        checked={c.completed}
                                        readOnly
                                        className="h-5 w-5 accent-primary"
                                    />
                                    <span className="font-medium text-gray-700 dark:text-gray-200">
                                        {c.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === "location" && (
                <div className="dashboard-card-straight border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                    <h2 className="section-title dark:text-white">
                        Location — {locationName}
                    </h2>

                    {!isLoaded ? (
                        <p className="dark:text-gray-300">Loading map…</p>
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{
                                width: "100%",
                                height: "350px",
                                borderRadius: "14px",
                            }}
                            center={mapCenter}
                            zoom={14}
                        >
                            <Marker position={mapCenter} />
                        </GoogleMap>
                    )}
                </div>
            )}

            {activeTab === "comments" && (
                <div className="dashboard-card-straight border border-gray-200 p-6 dark:border-gray-800 dark:bg-gray-900">
                    <h2 className="section-title mb-4 dark:text-white">Comments</h2>

                    {!task.comments?.length ? (
                        <p className="text-gray-500 dark:text-gray-400">No comments yet.</p>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-2">
                            {task.comments.map((c, i) => (
                                <div
                                    key={i}
                                    className="rounded-xl border bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800"
                                >
                                    <p className="text-sm font-semibold text-secondary dark:text-primary">
                                        {c.status}
                                    </p>
                                    <p className="mt-2 text-gray-700 dark:text-gray-300">
                                        {show(c.description)}
                                    </p>
                                    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                                        {c.time}
                                    </p>

                                    {c.images?.length > 0 && (
                                        <div
                                            onClick={() => openImageViewer(c.images)}
                                            className="group relative mt-4 h-40 cursor-pointer overflow-hidden rounded-xl border dark:border-gray-700"
                                        >
                                            <img
                                                src={c.images[0]}
                                                alt="preview"
                                                className="h-full w-full object-cover transition group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                <span className="rounded-full bg-black/60 px-3 py-1 text-white">
                                                    {c.images.length} image
                                                    {c.images.length > 1 ? "s" : ""}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* IMAGE VIEWER MODAL */}
            {viewerOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
                    <button
                        onClick={closeViewer}
                        className="absolute right-6 top-6 text-2xl text-white"
                    >
                        ✕
                    </button>

                    <button
                        onClick={prevImage}
                        className="absolute left-6 text-4xl text-white"
                    >
                        ‹
                    </button>

                    <img
                        src={viewerImages[viewerIndex]}
                        alt="full"
                        className="max-h-[80vh] max-w-[90vw] rounded-xl"
                    />

                    <button
                        onClick={nextImage}
                        className="absolute right-6 text-4xl text-white"
                    >
                        ›
                    </button>
                </div>
            )}

            {/* CONFIRM ACTION MODAL */}
            {confirmAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="w-full max-w-md rounded-xl bg-white p-6 dark:bg-gray-900">
                        <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                            Confirm {confirmAction.label}
                        </h3>

                        <p className="mb-6 text-gray-600 dark:text-gray-400">
                            Are you sure you want to proceed?
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmAction(null)}
                                className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-700 dark:text-gray-200"
                            >
                                Cancel
                            </button>

                            <button
                                disabled={actionLoading}
                                onClick={() =>
                                    handleStatusUpdate(
                                        confirmAction.fromStatus,
                                        confirmAction.toStatus
                                    )
                                }
                                className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
                            >
                                {actionLoading ? "Processing…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* COMMENT ACTION MODAL */}
            {commentAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="w-full max-w-md rounded-xl bg-white p-6 dark:bg-gray-900">
                        <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                            {commentAction.label}
                        </h3>

                        <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add a comment (required)"
                            className="h-28 w-full resize-none rounded-lg border border-gray-300 p-3 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                        />

                        <div className="mt-5 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setCommentAction(null);
                                    setCommentText("");
                                }}
                                className="rounded-lg border border-gray-300 px-4 py-2 dark:border-gray-700 dark:text-gray-200"
                            >
                                Cancel
                            </button>

                            <button
                                disabled={!commentText.trim() || actionLoading}
                                onClick={() =>
                                    handleStatusUpdate(
                                        commentAction.fromStatus,
                                        commentAction.toStatus,
                                        commentText
                                    )
                                }
                                className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
                            >
                                {actionLoading ? "Submitting…" : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}